/**
 * One of a person's own signatures in the "my signatures and sender" window:
 * its name, the default mark and the remove button, over the fields and the
 * live preview (`SignatureFields`).
 */
import { Trash2 } from "lucide-react";
import { Badge } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailCardPreview } from "../../../../lib/mail";
import type { MyMailSettings, OwnSignature } from "../../../../lib/mailSignature";
import { Btn, IconBtn } from "../../../ui";
import { Field } from "../../settings/panel/fields";
import { SignatureFields } from "./SignatureFields";

export function OwnSignatureRow({
  sig,
  isDefault,
  company,
  limits,
  onChange,
  onMakeDefault,
  onRemove,
}: {
  sig: OwnSignature;
  isDefault: boolean;
  company: MailCardPreview | null;
  limits: MyMailSettings["limits"];
  onChange: (part: Partial<OwnSignature>) => void;
  onMakeDefault: () => void;
  onRemove: () => void;
}) {
  const t = useT();

  return (
    <div className="space-y-3 rounded-2xl border border-[var(--crm-hair)] p-3.5">
      <div className="flex items-end gap-2">
        <Field label={t("myMail.label")} className="min-w-0 flex-1">
          <input
            className="crm-inp w-full"
            value={sig.label}
            maxLength={limits.label}
            placeholder={t("myMail.labelPlaceholder")}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </Field>
        <div className="flex h-[52px] flex-none items-center gap-1.5">
          {isDefault ? (
            <Badge variant="secondary">{t("myMail.default")}</Badge>
          ) : (
            <Btn small type="button" onClick={onMakeDefault}>
              {t("myMail.makeDefault")}
            </Btn>
          )}
          <IconBtn small type="button" title={t("myMail.remove")} aria-label={t("myMail.remove")} onClick={onRemove}>
            <Trash2 className="size-3.5" />
          </IconBtn>
        </div>
      </div>

      <SignatureFields sig={sig} company={company} limits={limits} onChange={onChange} />
    </div>
  );
}
